import { useState, type FormEvent } from 'react';

import { IconSearch } from '../../components/layout/icons';
import { ErrorState, LoadingState } from '../../components/ui/Feedback';
import { ExcelExportButton } from '../../components/ui/ExcelExportButton';
import { ApiError } from '../../lib/api-client';
import { exportRowsToExcel } from '../../lib/excel-export';
import { todayIso } from './report-filters';
import { useDailyReport } from './reports.queries';
import type { DailyReportMetric } from './types';

const displayDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

const formatCount = (value: number) => value.toLocaleString('en-IN');
const formatMoney = (value: number) => `₹${value.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const highestText = (metric: DailyReportMetric) => `${formatCount(metric.highest.count)}${metric.highest.month ? ` (${metric.highest.month})` : ''}`;
const todayText = (metric: DailyReportMetric) => metric.dayDetail
  ? `${formatCount(metric.today)} (N-${formatCount(metric.dayDetail.new)}, O-${formatCount(metric.dayDetail.old)})`
  : formatCount(metric.today);

const ReportRows = ({ rows, startAt = 1 }: { rows: DailyReportMetric[]; startAt?: number }) => (
  <tbody>
    {rows.map((row, index) => (
      <tr key={row.key}>
        <td>{startAt + index}</td>
        <td>{row.label}</td>
        <td>{highestText(row)}</td>
        <td>{formatCount(row.asOn)}</td>
        <td>{todayText(row)}</td>
      </tr>
    ))}
  </tbody>
);

export const DailyReportPage = () => {
  const [draftDate, setDraftDate] = useState(todayIso);
  const [date, setDate] = useState(todayIso);
  const query = useDailyReport(date);
  const reportDate = query.data?.date ?? date;
  const errorMessage = query.error instanceof ApiError ? query.error.message : 'Daily report could not be loaded.';

  const search = (event: FormEvent) => {
    event.preventDefault();
    setDate(draftDate);
  };

  const exportToExcel = async () => {
    if (!query.data) return;
    const rows = [...query.data.metrics, ...query.data.ipdStatus];
    await exportRowsToExcel({
      fileName: `Daily-Report-${reportDate}`,
      title: 'Daily Report',
      subtitle: `Report date: ${displayDate(reportDate)}`,
      columns: [
        { header: 'SL No.', width: 9, align: 'center', map: (_row, index) => index + 1 },
        { header: 'Particulars', width: 34, map: (row) => row.label },
        { header: 'Highest No.', width: 18, align: 'center', map: highestText },
        { header: `No. of Patient As On ${displayDate(reportDate)}`, width: 28, align: 'center', map: (row) => row.asOn },
        { header: 'No. of Patient For The Day', width: 28, align: 'center', map: todayText },
      ],
      rows,
    });
  };

  return (
    <div className="page-stack daily-report-page">
      <section className="content-card daily-report-card">
        <header className="daily-report-titlebar">
          <h1>DAILY REPORT</h1>
          <div className="daily-report-export-actions report-print-hidden">
            <button type="button" className="daily-report-pdf" title="Open the print dialog to save as PDF" onClick={() => window.print()}>PDF</button>
            <ExcelExportButton onExport={exportToExcel} disabled={!query.isSuccess} label="Excel" />
          </div>
        </header>

        <form className="daily-report-filter report-print-hidden" onSubmit={search}>
          <label>
            <span>Report date</span>
            <input type="date" required value={draftDate} max={todayIso()} onChange={(event) => setDraftDate(event.target.value)} />
          </label>
          <button type="submit" disabled={query.isFetching}><IconSearch />Search</button>
        </form>

        {query.isLoading ? <LoadingState rows={12} columns={5} label="Loading daily report" /> : null}
        {query.isError ? <ErrorState title="Unable to load daily report" message={errorMessage} action={{ label: 'Try again', onClick: () => void query.refetch() }} /> : null}
        {query.isSuccess ? (
          <>
            <div className="daily-report-table-scroll">
              <table className="daily-report-table">
                <thead>
                  <tr>
                    <th>SL NO.</th>
                    <th>PARTICULARS</th>
                    <th>HIGHEST NO.</th>
                    <th>NO. OF PATIENT AS ON {displayDate(reportDate)}</th>
                    <th>NO. OF PATIENT FOR THE DAY</th>
                  </tr>
                </thead>
                <ReportRows rows={query.data.metrics} />
              </table>
            </div>

            <h2 className="daily-report-section-title">IPD STATUS</h2>
            <div className="daily-report-table-scroll">
              <table className="daily-report-table">
                <thead>
                  <tr>
                    <th>SL NO.</th>
                    <th>PARTICULARS</th>
                    <th>HIGHEST NO.</th>
                    <th>NO. OF PATIENT AS ON {displayDate(reportDate)}</th>
                    <th>NO. OF PATIENT FOR THE DAY</th>
                  </tr>
                </thead>
                <ReportRows rows={query.data.ipdStatus} startAt={17} />
              </table>
            </div>

            <div className="daily-report-totals">
              <section>
                <h3>PRESENT IPD PATIENT AS ON {displayDate(reportDate)}</h3>
                <strong>{formatCount(query.data.presentIpd)}</strong>
              </section>
              <section>
                <h3>OPD OUTSTANDING</h3>
                <p>Today: {formatMoney(query.data.outstanding.opd.today)}</p>
                <p>Total: {formatMoney(query.data.outstanding.opd.total)}</p>
              </section>
              <section>
                <h3>IPD OUTSTANDING</h3>
                <p>Today: {formatMoney(query.data.outstanding.ipd.today)}</p>
                <p>Total: {formatMoney(query.data.outstanding.ipd.total)}</p>
              </section>
            </div>
          </>
        ) : null}
      </section>
    </div>
  );
};
